/**
 * PR 19y5 (UI-04) — Stored-XSS mitigation for rich HTML.
 *
 * Every place we render user-controlled HTML via
 * `dangerouslySetInnerHTML` MUST run the payload through this
 * sanitizer first. Tokens live in localStorage today (that migration
 * is Phase 3), so a single unsanitized blog post / email template /
 * splash announcement / legal doc could exfiltrate the admin JWT.
 *
 * The default profile is strict: no <script>, no inline event
 * handlers, no javascript: URLs, no <iframe>. Tables, headings,
 * lists, links, images (src whitelisted to http/https/data-image),
 * and text formatting are allowed — matches what the RichTextEditor
 * produces via Tiptap StarterKit + our custom extensions.
 */
import DOMPurify from 'dompurify';

// One shared instance — slightly cheaper + config is stable.
const _purify = DOMPurify;

// Force target=_blank + rel=noopener/noreferrer on all <a> so
// user-controlled content can't hijack the parent window.
_purify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

const _defaultConfig = {
  ALLOWED_TAGS: [
    'a', 'b', 'blockquote', 'br', 'code', 'div', 'em', 'figure',
    'figcaption', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i',
    'img', 'li', 'mark', 'ol', 'p', 'pre', 's', 'small', 'span',
    'strong', 'sub', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th',
    'thead', 'tr', 'u', 'ul',
  ],
  ALLOWED_ATTR: [
    'href', 'src', 'alt', 'title', 'target', 'rel',
    'colspan', 'rowspan', 'align',
    'class', 'style',
  ],
  // Allow http/https/mailto/tel/data-image; block javascript:, data:text/html.
  ALLOWED_URI_REGEXP:
    /^(?:(?:https?|mailto|tel|ftp):|data:image\/(?:png|jpeg|jpg|gif|webp|svg\+xml);base64,)/i,
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
};

export function sanitizeHtml(dirty, extraConfig = {}) {
  if (dirty == null) return '';
  const str = typeof dirty === 'string' ? dirty : String(dirty);
  return _purify.sanitize(str, { ..._defaultConfig, ...extraConfig });
}

// Convenience: strip ALL tags, return text only. Useful for previews.
export function sanitizeText(dirty) {
  if (dirty == null) return '';
  return _purify.sanitize(String(dirty), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
}

export default sanitizeHtml;
